import { computed, makeObservable } from 'mobx'

import { Model } from 'modules/core/lib/mobx-rest'

import { ScoreTypes } from 'modules/reviews/lib/enums'
import groups from 'modules/reviews/store/collections/groups'
import reviewForms from 'modules/reviews/store/collections/reviewForms'
import type Group from 'modules/reviews/store/models/Group'
import type ReviewForm from 'modules/reviews/store/models/ReviewForm'

import QuestionGroupActor from './QuestionGroupActor'

const Status = {
  active: 'active',
  closed: 'closed',
  completed: 'completed',
  draft: 'draft',
}

export interface CompetencyAssessment {
  id: number
  competency_id: number
  assessment_group_id: number
  competency_option_id: number | null
  description: string | null
}

interface IQuestionGroup {
  [key: string]: any
}

class QuestionGroup extends Model<IQuestionGroup> {
  static Status = Status

  constructor(attr: IQuestionGroup = {}) {
    super(attr)
    makeObservable(this, {
      isAnswered: computed,
      isClosed: computed,
      isActive: computed,
      isDraft: computed,
      respondent: computed,
      target: computed,
      respondentGroup: computed,
      form: computed,
      canSeeAnswers: computed,
      withCategory: computed,
      competenciesAssessments: computed,
      withoutCategoryWithoutType: computed,
      withoutCategoryWithScoreType: computed,
    })
  }

  get indexes() {
    return ['respondent_id', 'surveys_form_id']
  }

  static urlRoot() {
    return '/performance_reviews/question_groups'
  }

  get isAnswered(): boolean {
    return this.attributes.state === Status.completed
  }

  get isClosed(): boolean {
    return this.attributes.state === Status.closed
  }

  get isActive(): boolean {
    return this.attributes.state === Status.active
  }

  get isDraft(): boolean {
    return this.attributes.state === Status.draft
  }

  get respondent(): QuestionGroupActor | null | undefined {
    if (this.attributes.respondent) {
      return new QuestionGroupActor(
        this.attributes.respondent.id,
        this.attributes.respondent.full_name,
        this.attributes.respondent.first_name,
        this.attributes.respondent.avatar,
        this.attributes.respondent.job_title
      )
    }
  }

  get target(): QuestionGroupActor {
    return new QuestionGroupActor(
      this.attributes.target.id,
      this.attributes.target.full_name,
      this.attributes.target.first_name,
      this.attributes.target.avatar,
      this.attributes.target.job_title
    )
  }

  get respondentGroup(): Group | null | undefined {
    return groups.get(this.attributes.respondent_group_id)
  }

  get form(): ReviewForm | null | undefined {
    return reviewForms.find({
      id: this.attributes.surveys_form_id,
    })
  }

  get canSeeAnswers(): boolean {
    return this.attributes.can_see_answers
  }

  get withCategory(): Group[] {
    return this.attributes.questions.filter((q: any) => q.surveys_question_category_id)
  }

  get withoutCategoryWithoutType(): Group[] {
    return this.attributes.questions.filter(
      (q: any) => !q.surveys_question_category_id && !q.question_type
    )
  }

  get withoutCategoryWithScoreType(): Group[] {
    return this.attributes.questions.filter(
      (q: any) =>
        !q.surveys_question_category_id &&
        q.question_type &&
        Object.values(ScoreTypes).includes(q.question_type)
    )
  }

  get competenciesAssessments(): CompetencyAssessment[] {
    return this.attributes.competencies_assessments ?? []
  }
}

export default QuestionGroup
